/**
 * TicketHall API Service Layer
 * All Supabase calls go through here for portability to native apps.
 */
import { supabase } from "./supabase-client";
import { sanitizePostgrestFilter } from "./search";

// ============================================================
// EVENTS
// ============================================================

/** Fetch published events with optional filters */
export async function getEvents(filters?: {
  category?: string;
  city?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  let query = supabase
    .from("events")
    .select("*, profiles!events_producer_id_fkey(organizer_slug), ticket_tiers(price)")
    .eq("status", "published")
    .eq("visibility", "public")
    .gte("end_date", new Date().toISOString())
    .order("start_date", { ascending: true });

  if (filters?.category) query = query.eq("category", filters.category);
  if (filters?.city) query = query.eq("venue_city", filters.city);
  if (filters?.search) {
    const safeSearch = sanitizePostgrestFilter(filters.search);
    query = query.or(`title.ilike.%${safeSearch}%,description.ilike.%${safeSearch}%,venue_name.ilike.%${safeSearch}%,venue_city.ilike.%${safeSearch}%`);
  }
  if (filters?.limit) query = query.limit(filters.limit);
  if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/** Get event by slug. RLS controls visibility — published for everyone, own events for producers. */
export async function getEventBySlug(slug: string) {
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
  let query = supabase.from("events").select("*");
  
  if (isUUID) {
    query = query.or(`slug.eq.${slug},id.eq.${slug}`);
  } else {
    query = query.eq("slug", slug);
  }
  
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Evento não encontrado");
  return data;
}

export async function getEventBySlugWithProducer(slug: string) {
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
  let query = supabase
    .from("events")
    .select("*, profiles!events_producer_id_fkey(id, full_name, organizer_slug, organizer_logo_url, organizer_bio), organizations(id, name, slug, logo_url, description)");
  
  if (isUUID) {
    query = query.or(`slug.eq.${slug},id.eq.${slug}`);
  } else {
    query = query.eq("slug", slug);
  }
  
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Evento não encontrado");
  return data;
}

export async function getProducerByUsername(username: string) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("organizer_slug", username)
    .single();
  if (error) throw error;
  if (!data) throw new Error("Produtor não encontrado");
  return data;
}

/** Get ticket tiers for an event */
export async function getEventTiers(eventId: string) {
  const { data, error } = await supabase
    .from("ticket_tiers")
    .select("*")
    .eq("event_id", eventId)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  // Filter out explicitly invisible tiers, but include null (legacy data)
  return (data || []).filter((t: any) => t.is_visible !== false);
}

// ============================================================

/** Get user's orders */
export async function getMyOrders(userId: string) {
  const { data, error } = await supabase
    .from("orders")
    .select("*, events(title, slug, cover_image_url, start_date, venue_city)")
    .eq("buyer_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

// ============================================================
// TICKETS
// ============================================================

/** Get user's tickets */
export async function getMyTickets(userId: string) {
  const { data, error } = await supabase
    .from("tickets")
    .select("*, tier_id, ticket_tiers(name, price, is_resellable, is_transferable), events(title, slug, cover_image_url, start_date, end_date, venue_name, venue_address, venue_city, venue_state, is_online, online_url, streaming_url, streaming_redirect_token, description, has_certificates, certificate_config, allow_resale), orders(id, total, subtotal, platform_fee, discount_amount, created_at, payment_method, status, payment_status, boleto_url)")
    .eq("owner_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  if (!data || data.length === 0) return data;

  // Certificate opt-out prefs: per-ticket exclusion (ticket_id set) takes priority;
  // account-wide exclusion (ticket_id null) applies to every ticket of that user in the event.
  // Mirrors the logic in supabase/functions/dispatch-certificate-emails and generate-certificate.
  const ticketIds = data.map((t: any) => t.id);
  const { data: prefs } = await supabase
    .from("participant_certificate_prefs")
    .select("event_id, user_id, ticket_id, opt_out")
    .eq("opt_out", true)
    .or(`ticket_id.in.(${ticketIds.join(",")}),user_id.eq.${userId}`);

  if (prefs && prefs.length > 0) {
    const ticketOptOutIds = new Set(prefs.filter((p: any) => p.ticket_id).map((p: any) => p.ticket_id));
    const accountOptOutEventIds = new Set(
      prefs.filter((p: any) => !p.ticket_id && p.user_id === userId).map((p: any) => p.event_id)
    );
    for (const ticket of data as any[]) {
      ticket.certificate_opt_out =
        ticketOptOutIds.has(ticket.id) || accountOptOutEventIds.has(ticket.event_id);
    }
  }

  return data;
}

// ============================================================
// COUPONS
// ============================================================

/** Validate a coupon code for an event */
export async function validateCoupon(eventId: string, code: string) {
  const { data, error } = await supabase
    .from("coupons")
    .select("*")
    .eq("event_id", eventId)
    .eq("code", code.toUpperCase())
    .eq("is_active", true)
    .single();
  if (error) throw error;
  if (!data) throw new Error("Cupom não encontrado");

  // Client-side validation of temporal and usage limits
  const now = new Date();
  if (data.valid_from && new Date(data.valid_from) > now) {
    throw new Error("Este cupom ainda não está ativo.");
  }
  if (data.valid_until && new Date(data.valid_until) < now) {
    throw new Error("Este cupom expirou.");
  }
  if (data.max_uses !== null && (data.uses_count || 0) >= data.max_uses) {
    throw new Error("Este cupom atingiu o limite de usos.");
  }

  return data;
}

// ============================================================
// PROFILES
// ============================================================

/** Update user profile */
export async function updateProfile(userId: string, updates: Record<string, any>) {
  const { data, error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("id", userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ============================================================
// NOTIFICATIONS
// ============================================================

/** Linha de `notifications` como a web e os apps nativos consomem. */
export interface NotificationRow {
  id: string;
  user_id: string;
  type: string | null;
  title: string | null;
  body: string | null;
  data: Record<string, unknown> | null;
  is_read: boolean | null;
  created_at: string | null;
}

/** Get user's notifications */
export async function getNotifications(userId: string, limit = 10) {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as NotificationRow[];
}

/** Mark notifications as read */
export async function markNotificationsRead(userId: string) {
  const { error } = await supabase
    .from("notifications")
    .update({ is_read: true })
    .eq("user_id", userId)
    .eq("is_read", false);
  if (error) throw error;
}

// ============================================================
// WAITLIST
// ============================================================

/** Join waitlist */
export async function joinWaitlist(entry: {
  event_id: string;
  tier_id?: string;
  user_id: string;
  email: string;
  phone?: string;
}) {
  const { data, error } = await supabase
    .from("waitlist")
    .insert(entry)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ============================================================
// ANALYTICS (placeholder)
// ============================================================

/**
 * ANALYTICS_INTEGRATION_POINT — connect to Mixpanel, Amplitude, or Plausible
 */
export function trackEvent(eventName: string, properties?: Record<string, any>) {
  console.log(`[Analytics] Event: ${eventName}`, properties);
  
  // Example Mixpanel:
  // if (window.mixpanel) window.mixpanel.track(eventName, properties);
}

export function trackPageView(path: string) {
  console.log(`[Analytics] PageView: ${path}`);
  
  // Example GA4:
  // if (window.gtag) window.gtag('event', 'page_view', { page_path: path });
}

export function trackPurchase(orderId: string, value: number, items: any[]) {
  console.log(`[Analytics] Purchase: ${orderId}`, { value, items });
  
  trackEvent("purchase_completed", {
    order_id: orderId,
    total_value: value,
    items_count: items.length,
    items: items.map(i => ({ id: i.tierId, name: i.tierName, price: i.price }))
  });
}

// ============================================================
// CERTIFICATES
// ============================================================

/** Get user's certificates */
export async function getMyCertificates(userId: string) {
  const { data, error } = await supabase
    .from("certificates")
    .select("*, events(title, start_date, end_date, venue_name, certificate_config), ticket:tickets!certificates_ticket_id_fkey(attendee_cpf)")
    .eq("user_id", userId)
    .order("issued_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// ============================================================
// VIRTUAL QUEUE
// ============================================================

/** Get event info for virtual queue page */
export async function getEventForQueue(slug: string) {
  const { data, error } = await supabase
    .from("events")
    .select("id, title, cover_image_url, has_virtual_queue, queue_capacity, slug, profiles(organizer_slug)")
    .eq("slug", slug)
    .eq("status", "published")
    .single();
  if (error) throw error;
  return data;
}

/** Manage queue position (join, check status) */
export async function manageQueue(action: string, eventId: string, userId: string) {
  const { data, error } = await supabase.functions.invoke("manage-queue", {
    body: { action, eventId, userId },
  });
  if (error) throw error;
  return data;
}

